import React, { useState, useEffect } from 'react';
import '../styles/TaskSelectionModal.css';
import { useTasks } from '../hooks/useTasks';

const TaskSelectionModal = ({ show, onClose, onTaskSelect, userRole }) => {
  const { tasks: availableTasks, loading, error: fetchError } = useTasks(userRole);
  const [selectedTask, setSelectedTask] = useState('');
  const [customTask, setCustomTask] = useState('');
  const [useCustomTask, setUseCustomTask] = useState(false);
  const [error, setError] = useState('');

  // Reset state when modal opens
  useEffect(() => {
    if (show) {
      setSelectedTask('');
      setCustomTask('');
      setUseCustomTask(false);
      setError('');
    }
  }, [show]);

  // Set error message if fetch fails
  useEffect(() => {
    if (fetchError) {
      setError('Failed to load tasks. You can still enter a custom task.');
    }
  }, [fetchError]);

  const handleContinue = () => {
    const taskToUse = useCustomTask ? customTask.trim() : selectedTask;

    if (!taskToUse) {
      setError('Please select or enter a task description');
      return;
    }

    // Find the full task object if available
    const taskObj = availableTasks.find(t => t.task === taskToUse);

    onTaskSelect({
      task: taskToUse,
      description: taskObj?.description || taskToUse,
      category: taskObj?.category || 'custom'
    });

    onClose();
  };

  const handleSkip = () => {
    onTaskSelect(null);
    onClose();
  };

  if (!show) return null;

  return (
    <div className="modal-overlay" onClick={handleSkip}>
      <div className="modal-content task-modal" onClick={(e) => e.stopPropagation()}>
        <h2>What task are you working on?</h2>
        <p className="modal-description">
          Select a task to help us provide better workflow suggestions, or describe your own task.
        </p>

        {loading ? (
          <div className="loading-state">Loading tasks...</div>
        ) : (
          <>
            {availableTasks.length > 0 && (
              <>
                <div className="task-selection">
                  <label htmlFor="task-select">Select from suggested tasks:</label>
                  <select
                    id="task-select"
                    value={selectedTask}
                    onChange={(e) => {
                      setSelectedTask(e.target.value);
                      setUseCustomTask(false);
                      setError('');
                    }}
                    disabled={useCustomTask}
                  >
                    <option value="">-- Select a Task --</option>
                    {availableTasks.map((taskObj, idx) => (
                      <option key={idx} value={taskObj.task}>
                        {taskObj.task}
                        {taskObj.category && ` (${taskObj.category})`}
                      </option>
                    ))}
                  </select>
                  {selectedTask && availableTasks.find(t => t.task === selectedTask)?.description && (
                    <div className="task-description-hint">
                      {availableTasks.find(t => t.task === selectedTask).description}
                    </div>
                  )}
                </div>

                <div className="custom-task-divider">
                  <span>OR</span>
                </div>
              </>
            )}

            <div className="custom-task-section">
              <label htmlFor="custom-task">Enter your own task:</label>
              <textarea
                id="custom-task"
                maxLength={200}
                rows={3}
                value={customTask}
                onChange={(e) => {
                  setCustomTask(e.target.value);
                  if (e.target.value.trim()) {
                    setUseCustomTask(true);
                    setSelectedTask('');
                  } else {
                    setUseCustomTask(false);
                  }
                  setError('');
                }}
                placeholder="e.g., Analyze customer feedback for Q4 and create actionable insights..."
              />
              <div className="character-count">
                {customTask.length}/200 characters
              </div>
            </div>
          </>
        )}

        {error && <div className="error-message">{error}</div>}

        <div className="modal-actions">
          <button onClick={handleSkip} className="skip-button">
            Skip for Now
          </button>
          <button
            onClick={handleContinue}
            disabled={loading || (!selectedTask && !customTask.trim())}
            className="continue-button"
          >
            Continue
          </button>
        </div>
      </div>
    </div>
  );
};

export default TaskSelectionModal;
